/**
 * State Store
 * Simple reactive state management for the mini app
 */

import type {
  AppState,
  AppPage,
  RoomAvailability,
  GuestInfo,
  SearchCriteria,
  BookingConfirmation,
  UserProfile,
} from './types';

// LocalStorage key for saved guest info
const GUEST_INFO_STORAGE_KEY = 'lineoa_guest_info';

// Initial state
const initialState: AppState = {
  currentPage: 'search',
  language: 'th',
  user: null,
  search: null,
  availableRooms: [],
  selectedRoom: null,
  guestInfo: null,
  booking: null,
  loading: false,
  error: null,
  extraCharges: null,
  paymentIntentClientSecret: null,
  paymentIntentId: null,
};

// Current state
let state: AppState = { ...initialState };

// Subscribers
type Subscriber = (state: AppState) => void;
const subscribers: Set<Subscriber> = new Set();

/**
 * Get current state
 */
export function getState(): AppState {
  return state;
}

/**
 * Update state and notify subscribers
 */
export function setState(updates: Partial<AppState>): void {
  state = { ...state, ...updates };
  notifySubscribers();
}

/**
 * Subscribe to state changes
 */
export function subscribe(callback: Subscriber): () => void {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

/**
 * Notify all subscribers
 */
function notifySubscribers(): void {
  subscribers.forEach(callback => callback(state));
}

/**
 * Reset state to initial
 */
export function resetState(): void {
  state = { ...initialState };
  notifySubscribers();
}

// Action helpers

export function setUser(user: UserProfile): void {
  setState({ user, language: user.language as 'th' | 'en' | 'zh' });
}

export function setLanguage(language: 'th' | 'en' | 'zh'): void {
  setState({ language });
}

export function navigateTo(page: AppPage): void {
  setState({ currentPage: page, error: null });
}

export function setLoading(loading: boolean): void {
  setState({ loading });
}

export function setError(error: string | null): void {
  setState({ error, loading: false });
}

export function setSearch(search: SearchCriteria): void {
  setState({ search });
}

export function setAvailableRooms(rooms: RoomAvailability[]): void {
  setState({ availableRooms: rooms });
}

export function selectRoom(room: RoomAvailability): void {
  setState({ selectedRoom: room });
}

export function setGuestInfo(guestInfo: GuestInfo): void {
  setState({ guestInfo });
  // Note: Profile saving is now handled via API in confirm page after successful booking
}

/**
 * Save guest info to localStorage (fallback, may not persist in LINE LIFF)
 */
export function saveGuestInfoToStorage(guestInfo: GuestInfo): void {
  try {
    const dataToSave = {
      firstName: guestInfo.firstName,
      lastName: guestInfo.lastName,
      email: guestInfo.email,
      phone: guestInfo.phone,
      title: guestInfo.title || '',
      nationality: guestInfo.nationality || '',
      idType: guestInfo.idType || '',
      idNumber: guestInfo.idNumber || '',
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(GUEST_INFO_STORAGE_KEY, JSON.stringify(dataToSave));
    console.log('[Store] Guest info saved to localStorage (fallback):', dataToSave);
  } catch (e) {
    console.error('[Store] Failed to save guest info to localStorage:', e);
  }
}

/**
 * Load saved guest info from localStorage
 */
export function loadSavedGuestInfo(): Partial<GuestInfo> | null {
  try {
    console.log('[Store] Attempting to load guest info from localStorage...');
    console.log('[Store] Storage key:', GUEST_INFO_STORAGE_KEY);

    const saved = localStorage.getItem(GUEST_INFO_STORAGE_KEY);
    console.log('[Store] Raw localStorage value:', saved);

    if (!saved) {
      console.log('[Store] No saved guest info found');
      return null;
    }

    const data = JSON.parse(saved);
    console.log('[Store] Parsed data:', data);

    // Check if data is not too old (e.g., 90 days)
    if (data.savedAt) {
      const savedDate = new Date(data.savedAt);
      const now = new Date();
      const daysDiff = (now.getTime() - savedDate.getTime()) / (1000 * 60 * 60 * 24);
      console.log('[Store] Data age (days):', daysDiff);

      if (daysDiff > 90) {
        console.log('[Store] Saved guest info expired, clearing...');
        localStorage.removeItem(GUEST_INFO_STORAGE_KEY);
        return null;
      }
    }

    console.log('[Store] Returning saved guest info:', data);
    return {
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      title: data.title || undefined,
      nationality: data.nationality || undefined,
      idType: data.idType || undefined,
      idNumber: data.idNumber || undefined,
    };
  } catch (e) {
    console.error('[Store] Failed to load guest info from localStorage:', e);
    return null;
  }
}

/**
 * Clear saved guest info from localStorage
 */
export function clearSavedGuestInfo(): void {
  try {
    localStorage.removeItem(GUEST_INFO_STORAGE_KEY);
    console.log('[Store] Cleared saved guest info');
  } catch (e) {
    console.warn('[Store] Failed to clear guest info:', e);
  }
}

export function setBooking(booking: BookingConfirmation): void {
  setState({ booking });
}

export function setExtraCharges(extraCharges: AppState['extraCharges']): void {
  setState({ extraCharges });
}

/**
 * Calculate number of nights between check-in and check-out
 */
export function calculateNights(checkIn: string, checkOut: string): number {
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diff = end.getTime() - start.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Validate guest count against room capacity and calculate extra charges
 * Returns null if no extra charges, or an object with extra charge details
 */
export function validateCapacityAndCalculateCharges(
  room: RoomAvailability,
  search: SearchCriteria
): AppState['extraCharges'] {
  const { adults, children, rooms } = search;
  const maxAdults = room.maxAdults || 2;
  const maxChildren = room.maxChildren || 1;
  const extraAdultCharge = room.extraAdultCharge || 0;
  const extraChildCharge = room.extraChildCharge || 0;

  // Calculate guests per room (average)
  const adultsPerRoom = adults / rooms;
  const childrenPerRoom = children / rooms;

  // Calculate excess per room (can be fractional)
  const extraAdultsPerRoom = Math.max(0, adultsPerRoom - maxAdults);
  const extraChildrenPerRoom = Math.max(0, childrenPerRoom - maxChildren);

  // Total extra guests across all rooms
  const totalExtraAdults = Math.ceil(extraAdultsPerRoom * rooms);
  const totalExtraChildren = Math.ceil(extraChildrenPerRoom * rooms);

  if (totalExtraAdults === 0 && totalExtraChildren === 0) {
    return null; // No extra charges
  }

  // Calculate total extra charge
  const nights =
    search.checkOut && search.checkIn ? calculateNights(search.checkIn, search.checkOut) : 1;

  const totalExtraCharge =
    totalExtraAdults * extraAdultCharge * nights + totalExtraChildren * extraChildCharge * nights;

  return {
    extraAdults: totalExtraAdults,
    extraChildren: totalExtraChildren,
    extraAdultCharge,
    extraChildCharge,
    totalExtraCharge,
  };
}
